import { SvgIconComponent } from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BoltIcon from '@mui/icons-material/Bolt';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import SellIcon from '@mui/icons-material/Sell';
import PaletteIcon from '@mui/icons-material/Palette';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InsightsIcon from '@mui/icons-material/Insights';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PublicIcon from '@mui/icons-material/Public';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import { Theme } from '@mui/material/styles';
import { DemoPersonaRole } from '../../services/demoService';

/**
 * All copy and section data for the demo landing page, so the section components stay
 * presentational.
 *
 * The amber and purple accents are pinned here rather than read from palette.tertiary/accent:
 * tenant branding rewrites those palette slots at runtime (contexts/ThemeContext.tsx), and the
 * MathWiz tenant swaps secondary and tertiary outright (components/ranked/tierColors.ts). Blue
 * still comes from theme.palette.primary so the page follows the brand.
 */

export const AMBER = {
  main: '#F39C12',
  dark: '#D9820B',
  contrastText: '#231803',
} as const;

export const PURPLE = {
  main: '#9C27B0',
} as const;

export type DemoAccent = 'navy' | 'amber' | 'purple';

export const accentColor = (accent: DemoAccent, theme: Theme): string => {
  switch (accent) {
    case 'amber':
      return AMBER.main;
    case 'purple':
      return PURPLE.main;
    default:
      return theme.palette.primary.main;
  }
};

/** Anchor target for every "explore on your own" control. Stays on `/` — no new routes. */
export const PERSONA_DOORS_SECTION_ID = 'pick-a-role';

export const scrollToPersonaDoors = (): void => {
  document.getElementById(PERSONA_DOORS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth' });
};

export const CONTACT_EMAIL = 'contact@mathwizleague.com';

export const HERO = {
  overline: 'Interactive demo',
  // The no-break space keeps "on" from ending up alone on the last line on narrow screens.
  heading: 'The platform your math league runs on',
  sub: 'Timed quizzes, live leaderboards, badges and team play for students — scheduling, grading, payments and reports for the adults running the season. This is a working copy of the real platform, pre-loaded with a season at a fictional middle school.',
  primaryCta: 'Start the guided tour',
  primaryCtaLoading: 'Preparing your demo…',
  secondaryCta: 'Explore on your own',
  assurances: ['No sign-up', 'No password', 'Nothing you change is permanent'],
} as const;

export interface PersonaDoorContent {
  role: DemoPersonaRole;
  title: string;
  blurb: string;
  Icon: SvgIconComponent;
}

export const PERSONA_DOORS_HEADING = {
  overline: 'Pick a seat',
  heading: 'See it from every side',
  sub: 'One platform, four roles. Pick where you want to start — you can switch between all four at any time once you are inside.',
} as const;

export const PERSONA_DOORS: PersonaDoorContent[] = [
  {
    role: 'student',
    title: 'As a student',
    blurb: 'Play a timed quiz, watch the score land, and unlock a badge.',
    Icon: SchoolIcon,
  },
  {
    role: 'teacher',
    title: 'As a coach',
    blurb: 'Track a roster and find the students who are quietly falling behind.',
    Icon: GroupsIcon,
  },
  {
    role: 'parent',
    title: 'As a parent',
    blurb: 'Follow your children’s progress and enrol them in a session.',
    Icon: FamilyRestroomIcon,
  },
  {
    role: 'admin',
    title: 'As an administrator',
    blurb: 'Build a session, run the reports, and put your school’s branding on it.',
    Icon: SettingsIcon,
  },
];

export interface FeatureContent {
  title: string;
  description: string;
  Icon: SvgIconComponent;
  accent: DemoAccent;
}

export const FEATURES_HEADING = {
  overline: 'What’s inside',
  heading: 'Everything a season needs',
  sub: 'From the first enrollment to the final standings, the whole competition lives in one place.',
} as const;

export const FEATURES: FeatureContent[] = [
  {
    title: 'Sessions & scheduling',
    description:
      'Set up a competition season with flexible scheduling, individual or team enrollment, and deadlines the platform tracks for you.',
    Icon: CalendarMonthIcon,
    accent: 'navy',
  },
  {
    title: 'Timed & live quizzes',
    description:
      'Fast-paced rounds with per-question timers, speed-based scoring and live results — plus a lobby, a teacher console and a ranked ladder for running them live.',
    Icon: BoltIcon,
    accent: 'amber',
  },
  {
    title: 'Leaderboards, XP & badges',
    description:
      'Real-time standings, an XP ledger and unlockable achievements give students a reason to come back tomorrow.',
    Icon: EmojiEventsIcon,
    accent: 'purple',
  },
  {
    title: 'Team play',
    description:
      'Captains, invitations and team enrollments — run team events alongside individual ones without extra bookkeeping.',
    Icon: Diversity3Icon,
    accent: 'purple',
  },
  {
    title: 'Enrollment & payments',
    description:
      'Families enroll and pay in one flow: PayPal checkout, bundle pricing and discount codes are built in.',
    Icon: SellIcon,
    accent: 'navy',
  },
  {
    title: 'Per-school branding',
    description:
      'Each school gets its own logo, colors, fonts and domain — the platform wears your name, not ours.',
    Icon: PaletteIcon,
    accent: 'amber',
  },
];

export interface ProofPointContent {
  title: string;
  description: string;
  Icon: SvgIconComponent;
}

export const PROOF_POINTS: ProofPointContent[] = [
  {
    title: 'Four roles, one platform',
    description:
      'Students compete, coaches run the roster, parents follow along, administrators see the whole picture.',
    Icon: SwitchAccountIcon,
  },
  {
    title: 'Graded in the moment',
    description:
      'Every submission is scored automatically the instant it lands, so feedback reaches students immediately.',
    Icon: TaskAltIcon,
  },
  {
    title: 'Each school its own space',
    description:
      'Role-based access control and per-school data isolation keep every organization’s data its own.',
    Icon: VerifiedUserIcon,
  },
  {
    title: 'The whole season, measured',
    description:
      'Teacher analytics and administrator reports show who is progressing, who is stuck, and where the season stands.',
    Icon: InsightsIcon,
  },
];

export interface AudienceContent {
  title: string;
  description: string;
  Icon: SvgIconComponent;
  accent: DemoAccent;
}

export const AUDIENCE_HEADING = {
  overline: 'Who it’s for',
  heading: 'Built for the people who run math programs',
} as const;

export const AUDIENCES: AudienceContent[] = [
  {
    title: 'Schools & districts',
    description: 'Run a school or district league with enrollment, scheduling and standings in one place.',
    Icon: AccountBalanceIcon,
    accent: 'navy',
  },
  {
    title: 'Tutoring centers',
    description: 'Add structured competition to your program without adding administrative work.',
    Icon: AutoStoriesIcon,
    accent: 'amber',
  },
  {
    title: 'Math organizations',
    description: 'Host recurring competitions across many teams and schools under one roof.',
    Icon: PublicIcon,
    accent: 'purple',
  },
  {
    title: 'Independent coaches',
    description: 'Give a small roster the same season a big program gets.',
    Icon: CoPresentIcon,
    accent: 'navy',
  },
];

export const CTA = {
  overline: 'Ready when you are',
  heading: 'The demo is the pitch',
  sub: 'The fastest way to evaluate MathWiz League is from the inside. Pick a role and poke at everything — nothing you change is permanent, and nothing asks for a password.',
  primaryCta: 'Pick a role and step inside',
  secondaryCta: CONTACT_EMAIL,
} as const;

export const FOOTER = {
  tagline: 'Competition math for middle school.',
} as const;
